import Dexie from "dexie";
import { uuid } from "@/lib/uuid";

/**
 * This device's interaction log.
 *
 * Its own database, not a table in VibezPOSOffline: that one has tables cleared and
 * re-put on bootstrap, and a log write must never contend with order data or be
 * caught in its schema upgrades.
 *
 * `synced` is a number because IndexedDB cannot index booleans:
 *   0 = waiting to upload
 *   1 = the server acknowledged it
 *   2 = the server refused it (kept, counted, never retried — see sync.js)
 *
 * Retention rule (owner, 2026-09-17): a working day's logs stay on the device until
 * that day is over, and an entry the server has not acknowledged is never deleted.
 * `purgeEligible` is the only delete in this module, and it only takes entries that
 * are acknowledged AND from a finished business day.
 */
export const SYNC_WAITING = 0;
export const SYNC_DONE = 1;
export const SYNC_REFUSED = 2;

export const logDb = new Dexie("VibezInteractionLogs");
logDb.version(1).stores({
  entries: "seq, business_date, synced, [synced+business_date]",
  meta: "key",
});

export async function getMeta(key, fallback = null) {
  const row = await logDb.meta.get(key);
  return row === undefined ? fallback : row.value;
}

export async function setMeta(key, value) {
  await logDb.meta.put({ key, value });
}

/** The id this store uploads under. Minted once per database, so a wiped device starts a new stream. */
export async function ensureStreamId() {
  return logDb.transaction("rw", logDb.meta, async () => {
    const existing = await logDb.meta.get("stream_id");
    if (existing?.value) return existing.value;
    const created = uuid();
    await logDb.meta.put({ key: "stream_id", value: created });
    return created;
  });
}

/**
 * Store entries, numbering them inside the transaction.
 *
 * A till often has two tabs open (billing and KDS) sharing this database. Numbering
 * from each tab's own memory would hand both tabs the same sequence numbers; reading
 * and advancing `next_seq` in the same transaction as the insert cannot collide,
 * because IndexedDB serialises overlapping read-write transactions across tabs.
 */
export async function appendEntries(entries) {
  if (!entries.length) return [];
  return logDb.transaction("rw", logDb.entries, logDb.meta, async () => {
    const row = await logDb.meta.get("next_seq");
    let seq = row?.value || 1;
    const numbered = entries.map(entry => ({ ...entry, seq: seq++, synced: SYNC_WAITING }));
    await logDb.entries.bulkAdd(numbered);
    await logDb.meta.put({ key: "next_seq", value: seq });
    return numbered;
  });
}

/** The oldest waiting entries, in the order they happened. */
export function waitingBatch(limit) {
  return logDb.entries.where("synced").equals(SYNC_WAITING).limit(limit).toArray();
}

export function waitingCount() {
  return logDb.entries.where("synced").equals(SYNC_WAITING).count();
}

export function refusedCount() {
  return logDb.entries.where("synced").equals(SYNC_REFUSED).count();
}

export async function markSynced(seqs, syncedAt = new Date().toISOString()) {
  if (!seqs.length) return 0;
  return logDb.entries.where("seq").anyOf(seqs).modify({ synced: SYNC_DONE, synced_at: syncedAt });
}

export async function markRefused(seq, reason) {
  return logDb.entries.update(seq, { synced: SYNC_REFUSED, refused_reason: String(reason).slice(0, 500) });
}

/** Delete what is both acknowledged and from a finished day. Returns how many went. */
export function purgeEligible(todayBusinessDate) {
  return logDb.entries
    .where("[synced+business_date]")
    .between([SYNC_DONE, Dexie.minKey], [SYNC_DONE, todayBusinessDate], true, false)
    .delete();
}

/** Per business day: how many entries this device holds and how many still wait. */
export async function daysOnDevice() {
  const dates = await logDb.entries.orderBy("business_date").uniqueKeys();
  const days = await Promise.all(
    dates.map(async businessDate => {
      const [total, waiting, refused] = await Promise.all([
        logDb.entries.where("business_date").equals(businessDate).count(),
        logDb.entries.where("[synced+business_date]").equals([SYNC_WAITING, businessDate]).count(),
        logDb.entries.where("[synced+business_date]").equals([SYNC_REFUSED, businessDate]).count(),
      ]);
      return { businessDate, total, waiting, refused };
    })
  );
  return days.sort((a, b) => (a.businessDate < b.businessDate ? 1 : -1));
}

export async function entriesForDay(businessDate) {
  const rows = await logDb.entries.where("business_date").equals(businessDate).toArray();
  return rows.sort((a, b) => a.seq - b.seq);
}
